import React, { useState, useEffect, useCallback } from 'react';
import { Dropdown, DropDownButton } from '@/shared/components/Dropdown';
import { listOfferApiService } from '@/epics/offerManagment/api/listOfferApiService';
import { viewOffer } from '@/epics/offerManagment/api/viewOfferApiService';
import { MenuItemProps } from '@/shared/components/Menu/types';
import type { OfferListItem } from '@/epics/offerManagment/types/offerListType';

interface OfferInfiniteDropdownProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  hideChevron?: boolean;
}

export const OfferInfiniteDropdown: React.FC<OfferInfiniteDropdownProps> = ({
  value,
  onChange,
  disabled,
  hideChevron,
}) => {
  const [offers, setOffers] = useState<OfferListItem[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [resolvedOfferName, setResolvedOfferName] = useState<string>('');

  // Fetch selected offer by ID when it's not yet in the paginated list (e.g. edit mode, offer is page 10+)
  useEffect(() => {
    if (!value) {
      setResolvedOfferName('');
      return;
    }
    let mounted = true;
    viewOffer({ offerId: value }).then((result) => {
      if (mounted && result.success && result.data) {
        setResolvedOfferName(result.data.offerName || value);
      }
    });
    return () => {
      mounted = false;
    };
  }, [value]);

  // Initial load
  useEffect(() => {
    let mounted = true;
    const fetchFirstPage = async () => {
      setLoading(true);
      try {
        const response = await listOfferApiService.listOffers({
          page: 1,
          limit: 10,
        });
        if (mounted) {
          setOffers(response.data);
          setHasMore(response.hasNextPage);
          setPage(1);
        }
      } catch (error) {
        console.error('Error fetching offers', error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchFirstPage();

    return () => {
      mounted = false;
    };
  }, []);

  const fetchMore = useCallback(async () => {
    if (loading || !hasMore) {
      return;
    }
    setLoading(true);
    const nextPage = page + 1;
    try {
      const response = await listOfferApiService.listOffers({
        page: nextPage,
        limit: 10,
      });
      setOffers((prev) => {
        const newItems = response.data.filter(
          (newItem) => !prev.some((item) => item.id === newItem.id)
        );
        return [...prev, ...newItems];
      });
      setHasMore(response.hasNextPage);
      setPage(response.page);
    } catch (error) {
      console.error('Error fetching more offers', error);
    } finally {
      setLoading(false);
    }
  }, [loading, hasMore, page]);

  const items: MenuItemProps[] = offers.map((offer) => ({
    id: offer.id,
    label: offer.offerName || offer.id,
  }));

  if (loading) {
    items.push({ id: 'loading', label: 'załadunek...', disabled: true });
  }

  const selectedOffer = offers.find((o) => o.id === value);
  const displayValue = selectedOffer
    ? selectedOffer.offerName || selectedOffer.id
    : resolvedOfferName || value || '';

  return (
    <Dropdown
      menuFullWidth
      open={isDropdownOpen}
      onOpenChange={setIsDropdownOpen}
      items={items}
      selectedId={value}
      onItemClick={(id) => {
        if (id !== 'loading') {
          onChange(id);
          setIsDropdownOpen(false);
        }
      }}
      toolTip={false}
      trigger={({ open, toggle, disabled: dropDisabled, error }) => (
        <DropDownButton
          label="Oferta"
          value={displayValue}
          open={open}
          toggle={toggle}
          disabled={Boolean(disabled || dropDisabled)}
          error={error}
          toolTip={false}
          hideChevron={hideChevron ?? false}
        />
      )}
      disabled={disabled ?? false}
      onScrollEnd={() => {
        if (hasMore && !loading) {
          fetchMore();
        }
      }}
    />
  );
};
